import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Lock, Sparkles, Zap, Flame, Skull } from "lucide-react";

const POOL_COLOR = { mana: "#4A7BC7", vigor: "#B89C3A" };
const ELEMENT_COLOR = {
  Fire: "#E55A00", Ice: "#8FD1E5", Lightning: "#F7D64A", Poison: "#7CB342",
  Darkness: "#5D2F5F", Holy: "#E5C34A", Arcane: "#A97AB0", Earth: "#8B6E47",
  Water: "#4A6FA5", Wind: "#B0C4B1",
};

/**
 * Class-locked spellbook, unlockable at fixed levels (see game_data.CLASS_SKILLS).
 * Shown as a section inside SkillsPanel.
 */
export default function ClassSkills({ ch }) {
  const [data, setData] = useState(null);

  useEffect(() => { api.classSkills().then(setData).catch(() => {}); }, [ch.level, ch.chosen_class]);

  if (!data) return null;
  return (
    <div className="panel p-3" data-testid="class-skills">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} />
        <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
          Class Skills — {data.class}
        </span>
        <span className="ml-auto font-mono text-[10px] text-[#666]">Lv {data.level}</span>
      </div>
      <div className="space-y-1">
        {data.skills.map(s => {
          const locked = !s.unlocked;
          const Icon = s.cost_pool === "mana" ? Sparkles : Zap;
          const color = POOL_COLOR[s.cost_pool] || "#8c8c8c";
          return (
            <div
              key={s.id}
              data-testid={`skill-${s.id}`}
              data-locked={locked ? "1" : "0"}
              className={`panel-inner p-2 border-l-2 ${locked ? "opacity-45" : ""}`}
              style={{ borderColor: locked ? "#333" : color }}
            >
              <div className="flex items-center gap-2">
                {locked ? (
                  <Lock className="w-3 h-3 text-[#666]" strokeWidth={1.5} />
                ) : (
                  <Icon className="w-3 h-3" style={{ color }} strokeWidth={1.5} />
                )}
                <span className="font-heading text-sm text-[#E2DFD8]">{s.name}</span>
                <span className="ml-auto font-mono text-[9px] tracking-widest uppercase" style={{ color: locked ? "#666" : color }}>
                  {locked ? `Unlocks at Lv ${s.unlock}` : `${s.cost} ${s.cost_pool}`}
                </span>
              </div>
              <div className="font-body text-[11px] text-[#B0ADA5] italic mt-1">{s.desc}</div>
              {/* §14: DoT + Elemental badges — always shown when configured. */}
              {(s.applies_dot || s.deals_elemental) && (
                <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`skill-badges-${s.id}`}>
                  {s.applies_dot && (
                    <span data-testid={`skill-dot-${s.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#8A0303] text-[#E33333] bg-[#8A0303]/10 font-mono text-[9px] tracking-wider uppercase rounded-sm">
                      <Skull className="w-2.5 h-2.5" strokeWidth={2} />
                      DoT {s.dot_damage || 0}/{s.dot_duration || 3}t
                    </span>
                  )}
                  {s.deals_elemental && s.element && (
                    <span data-testid={`skill-element-${s.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono text-[9px] tracking-wider uppercase rounded-sm"
                      style={{
                        borderColor: ELEMENT_COLOR[s.element] || "#E5C34A",
                        color: ELEMENT_COLOR[s.element] || "#E5C34A",
                        backgroundColor: (ELEMENT_COLOR[s.element] || "#E5C34A") + "12",
                      }}>
                      <Flame className="w-2.5 h-2.5" strokeWidth={2} />
                      +{s.element_damage || 0} {s.element}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
